import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft, Star, Image as ImageIcon, Upload, Loader2, Hash, ToggleLeft, ToggleRight } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { uploadFileToS3, generateHeroFeaturedCollectionImageKey, getS3BucketName } from "@/lib/s3";

export default function AdminHeroFeaturedCollectionNew() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const collectionId = params.id ? parseInt(params.id) : null;
  const isEditMode = !!collectionId;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [collectionCreated, setCollectionCreated] = useState(false);
  const [createdCollectionId, setCreatedCollectionId] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    product_id: "",
    name: "",
    slug: "",
    description: "",
    gradient: "",
    emoji: "",
    filtervalue: "",
    display_order: "0",
    is_active: true,
  });

  useEffect(() => {
    if (isEditMode && token && collectionId) {
      fetchCollection();
    }
  }, [isEditMode, token, collectionId]);

  const fetchCollection = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getHeroFeaturedCollection(collectionId!, token!);
      setFormData({
        product_id: data.product_id ? String(data.product_id) : "",
        name: data.name || "",
        slug: data.slug || "",
        description: data.description || "",
        gradient: data.gradient || "",
        emoji: data.emoji || "",
        filtervalue: data.filtervalue || "",
        display_order: String(data.display_order || 0),
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
      if (data.image_url) {
        setImagePreview(data.image_url);
      }
    } catch (error: any) {
      console.error("Error fetching hero featured collection:", error);
      toast.error(error.response?.data?.detail || "Failed to load hero featured collection");
      navigate("/admin/hero-featured-collections");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({ ...formData, name });
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      setFormData((prev) => ({ ...prev, slug: generateSlug(name) }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    // If collection not created yet, show error
    if (!collectionCreated && !isEditMode) {
      toast.error("Please save the collection first before uploading an image");
      return;
    }

    setUploading(true);

    try {
      // Get S3 bucket name from environment variables
      const bucketName = getS3BucketName();

      const currentId = isEditMode ? collectionId! : createdCollectionId;
      if (!currentId) {
        throw new Error("Collection ID not available");
      }

      // Generate S3 key for hero featured collection image
      const s3Key = generateHeroFeaturedCollectionImageKey(currentId, file.name);

      // Upload file directly to S3
      const imageUrl = await uploadFileToS3(file, s3Key, bucketName);

      // Update collection with image URL
      await adminApi.updateHeroFeaturedCollection(
        currentId,
        { image_url: imageUrl },
        token!
      );

      toast.success("Image uploaded successfully!");
      setImagePreview(imageUrl);
      if (isEditMode) {
        await fetchCollection(); // Refresh collection data
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploading(false);
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Collection name is required");
      return;
    }

    setLoading(true);

    try {
      const collectionData: any = {
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        description: formData.description || null,
        gradient: formData.gradient || null,
        emoji: formData.emoji || null,
        filtervalue: formData.filtervalue || null,
        display_order: parseInt(formData.display_order) || 0,
        is_active: formData.is_active,
      };

      // Add product_id only if provided
      if (formData.product_id) {
        collectionData.product_id = parseInt(formData.product_id);
      }

      let response;
      if (isEditMode) {
        response = await adminApi.updateHeroFeaturedCollection(collectionId!, collectionData, token);
      } else {
        response = await adminApi.createHeroFeaturedCollection(collectionData, token);
      }

      // Handle response format
      const collection = response.data || response;
      const collectionIdFromResponse = collection.id || collectionId;

      if (collectionIdFromResponse) {
        toast.success(isEditMode ? "Hero featured collection updated successfully!" : "Hero featured collection created successfully!");
        setCollectionCreated(true);
        setCreatedCollectionId(collectionIdFromResponse);
        if (!isEditMode) {
          // Navigate to edit page with the new ID
          navigate(`/admin/hero-featured-collections/${collectionIdFromResponse}`);
        } else {
          await fetchCollection();
        }
      } else {
        toast.error("Failed to save hero featured collection");
      }
    } catch (error: any) {
      console.error("Error saving hero featured collection:", error);
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || "Failed to save hero featured collection");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse" />
          <div>
            <div className="h-7 w-48 bg-gray-200 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-64 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-4" />
              <div className="space-y-3">
                <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/hero-featured-collections"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? "Edit Hero Featured Collection" : "Add New Hero Featured Collection"}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditMode ? "Update hero featured collection details" : "Create a new hero featured collection for homepage"}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {loading ? "Saving..." : isEditMode ? "Save Changes" : "Save Collection"}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Basic Information</h2>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Collection Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., Summer Collection"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., summer-collection"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-generated from name if left empty
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="Brief description of the collection..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Product ID (Optional)
                </label>
                <input
                  type="number"
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="Link to a specific product"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Link this collection to a specific product
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image Upload */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <ImageIcon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Collection Image</h2>
              </div>
            </div>
            <div className="p-4">
              {imagePreview ? (
                <div className="space-y-3">
                  <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={imagePreview}
                      alt="Collection preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <label className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-700">Replace Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading || (!collectionCreated && !isEditMode)}
                    />
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-2">
                      {isEditMode || collectionCreated
                        ? "No image uploaded yet"
                        : "Save the collection first to upload an image"}
                    </p>
                    {isEditMode || collectionCreated ? (
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 cursor-pointer transition-colors">
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Upload Image</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    ) : (
                      <p className="text-xs text-gray-500">Click "Save Collection" first</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Additional Options */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Hash className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Additional Options</h2>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Gradient (Tailwind CSS)
                </label>
                <input
                  type="text"
                  name="gradient"
                  value={formData.gradient}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., from-aarsa-sage-500 to-aarsa-gold-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional Tailwind CSS gradient classes
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Emoji
                </label>
                <input
                  type="text"
                  name="emoji"
                  value={formData.emoji}
                  onChange={handleInputChange}
                  maxLength={10}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., ✨"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional emoji icon (max 10 characters)
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Filter Value
                </label>
                <input
                  type="text"
                  name="filtervalue"
                  value={formData.filtervalue}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., collection=calm or category=earrings"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Filter value to use when "Explore Collection" is clicked (e.g., collection=calm, category=earrings)
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Display Order
                </label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower numbers appear first in listings
                </p>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-0.5">
                    Active Status
                  </label>
                  <p className="text-xs text-gray-500">
                    {formData.is_active
                      ? "Collection is visible on homepage"
                      : "Collection is hidden from homepage"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, is_active: !formData.is_active })
                  }
                  className="flex items-center gap-2"
                >
                  {formData.is_active ? (
                    <ToggleRight className="w-6 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </form>
    </div>
  );
}

